/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import reportStatements from "../assets/reportStatements.json";
import fetchHelper from "../services/fetchHelper";
import { featureCheck } from "./feature";
import startCase from "lodash/startCase";
import { PAMV1RestUrl, PAMV2RestUrl } from "../constants/PatientManagementConstants";

const ERROR_WITH_PAM_API_STATUS =
  "There has been an issue while retrieving the anticoagulant status from the patient.";
const ERROR_WITH_PAM_API_STATEMENTS =
  "There has been an issue while retrieving the anticoagulant statements from the patient.";

export const findConclusionStatementsFromIds = (ids) => {
  let result = {};

  if (!featureCheck("anticoagulantStatement.enable")) return result;

  if (ids === undefined || ids.length === 0) return result;

  reportStatements.forEach((statement) => {
    statement.esignStatements.forEach((eStatement) => {
      if (ids.some((id) => Number(id) === eStatement.id)) {
        result[eStatement.id] = eStatement.conclusionStatement;
      }
    });
  });

  return result;
};

const getStatementsFromStatus = async (status) => {
  if (!featureCheck("anticoagulantStatement.enable")) return [];

  try {
    const data = await fetchHelper.get({
      url: `${PAMV1RestUrl}/anticoagulant-configuration/statements?status=${status}&isSelectedByDefault=false`,
    });

    return data;
  } catch (err) {
    throw Error(ERROR_WITH_PAM_API_STATUS);
  }
};

export const retrieveAnticoagulantIds = async (acStatus) => {
  let promises = [];

  if (!featureCheck("anticoagulantStatement.enable")) return {};

  let result = {};
  acStatus.forEach((current) => {
    promises.push(
      getStatementsFromStatus(current.id) //add each promise to the array
        .then((response) => {
          const res = {};
          res[current.text] = response.map((ja) => ja.id);
          return res;
        })
    );

    result[current.text] = [];
  });

  let listOfStatements = await Promise.all(promises); //wait for all the promises to finish (returns a promise)
  return Object.assign({}, ...listOfStatements);
};

const getAnticoagulantStatement = async (patientId, version) => {
  if (!featureCheck("anticoagulantStatement.enable")) return "";

  try {
    const { data } = await fetchHelper.getV2({
      url: `${version}/patient/${patientId}/anticoagulant-configuration`,
    });

    return data;
  } catch (err) {
    throw Error(ERROR_WITH_PAM_API_STATEMENTS);
  }
};

export const getAnticoagulantStatementV1 = async (patientId) => {
  return getAnticoagulantStatement(patientId, PAMV1RestUrl);
};

export const getAnticoagulantStatementV2 = async (patientId) => {
  return getAnticoagulantStatement(patientId, PAMV2RestUrl);
};

export const fetchUserById = async (idUser) => {
  const { name, family } = await fetchHelper.get({
    url: `/patientmanagement/rest/users/${idUser}/loadUser`,
  });

  return `${startCase(name)} ${startCase(family)}`;
};

export const getAllStatus = async () => {
  if (!featureCheck("anticoagulantStatement.enable"))
    return [
      {
        id: "1",
        text: "YES",
      },
      {
        id: "2",
        text: "NO",
      },
      {
        id: "3",
        text: "UNKNOWN",
      },
    ];

  try {
    const data = await fetchHelper.get({
      url: `${PAMV1RestUrl}/anticoagulant-configuration/statements/status`,
    });

    return data;
  } catch (err) {
    console.log(err);

    throw Error(ERROR_WITH_PAM_API_STATUS);
  }
};

const reportStatementUtil = {
  findConclusionStatementsFromIds,
  retrieveAnticoagulantIds,
  getAnticoagulantStatement,
  getAnticoagulantStatementV1,
  getAnticoagulantStatementV2,
  getAllStatus,
};

export default reportStatementUtil;
